import { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import AuthPage from './pages/AuthPage';
import WelcomePage from './pages/WelcomePage';
import FeedPage from './pages/FeedPage';
import AdminPage from './pages/AdminPage';
import ProfilePage from './pages/ProfilePage';
import ProfileEditPage from './pages/ProfileEditPage';
import SavedPostsPage from './pages/SavedPostsPage';
import UsersListPage from './pages/UsersListPage';
import ContributorPortalPage from './pages/ContributorPortalPage';
import AdminReminders from './pages/AdminReminders';
import MobileAdminPost from './pages/MobileAdminPost';
import TelegramSetup from './pages/TelegramSetup';
import AdminManagement from './pages/AdminManagement';
import AIAgent from './pages/AIAgent';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import PaymentCancelPage from './pages/PaymentCancelPage';
import { Toaster } from 'sonner';

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
      console.log('[App.js] User loaded from localStorage:', parsedUser);
    }
    if (storedToken && !token) {
      setToken(storedToken);
      console.log('[App.js] Token loaded from localStorage');
    }
    setLoading(false);
  }, []);

  const handleAuth = (authToken, userData) => {
    localStorage.setItem('token', authToken);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(authToken);
    setUser(userData);
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
  };

  const updateUser = (userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  return (
    <div className="App">
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#050505', color: '#EE1C4E' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold' }}>RedChannel</div>
            <div style={{ fontSize: '14px', color: '#A1A1A1', marginTop: '8px' }}>Loading...</div>
          </div>
        </div>
      ) : (
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={
              !token ? (
                <AuthPage onAuth={handleAuth} />
              ) : (
                <Navigate to="/welcome" replace />
              )
            }
          />
          <Route
            path="/reset-password"
            element={<ResetPasswordPage />}
          />
          <Route
            path="/payment/success"
            element={<PaymentSuccessPage />}
          />
          <Route
            path="/payment/cancel"
            element={<PaymentCancelPage />}
          />
          <Route
            path="/welcome"
            element={
              token ? (
                <WelcomePage user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/feed"
            element={
              token ? (
                <Navigate to="/feed/home" replace />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/feed/:feedSlug"
            element={
              <FeedPage user={user} onLogout={handleLogout} onUpdateUser={updateUser} />
            }
          />
          <Route
            path="/admin"
            element={
              (() => {
                const hasToken = !!token;
                const hasUser = !!user;
                const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
                console.log('[App.js] Admin route check:', { hasToken, hasUser, userRole: user?.role, isAdmin });
                
                if (hasToken && hasUser && isAdmin) {
                  return <AdminPage user={user} onLogout={handleLogout} />;
                } else if (hasToken) {
                  console.log('[App.js] Redirecting to feed - user not admin');
                  return <Navigate to="/feed" replace />;
                } else {
                  console.log('[App.js] Redirecting to login - no token');
                  return <Navigate to="/" replace />;
                }
              })()
            }
          />
          <Route
            path="/profile"
            element={
              token ? (
                <ProfilePage user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/profile/edit"
            element={
              token ? (
                <ProfileEditPage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/saved-posts"
            element={
              token ? (
                <SavedPostsPage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/contributor-portal"
            element={
              token ? (
                <ContributorPortalPage />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/admin/users"
            element={
              token && user?.role === 'super_admin' ? (
                <UsersListPage />
              ) : (
                <Navigate to="/feed" replace />
              )
            }
          />
          <Route
            path="/admin/reminders"
            element={
              token && user?.role === 'super_admin' ? (
                <AdminReminders user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/feed" replace />
              )
            }
          />
          <Route
            path="/admin/manage-admins"
            element={
              token && user?.role === 'super_admin' ? (
                <AdminManagement user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/feed" replace />
              )
            }
          />
          <Route
            path="/ai-agent"
            element={
              token && user?.role === 'super_admin' ? (
                <AIAgent user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/feed" replace />
              )
            }
          />
          <Route
            path="/mobile-post"
            element={
              token && (user?.role === 'admin' || user?.role === 'super_admin') ? (
                <MobileAdminPost user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/feed" replace />
              )
            }
          />
          <Route
            path="/telegram-setup"
            element={
              token && (user?.role === 'admin' || user?.role === 'super_admin') ? (
                <TelegramSetup user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/feed" replace />
              )
            }
          />
        </Routes>
      </BrowserRouter>
      )}
      <Toaster position="top-center" theme="dark" richColors />
    </div>
  );
}

export default App;